import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAutomationTemplateQuery, useUnarchiveAutomationTemplateMutation } from '../../../queries/Automation';
import { canRestoreAutomationTemplate } from '../../components/automation/permissions';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { Button } from '../../components-v2/Button';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { AppDrawer, AppDrawerToolbar } from '../../components-v2/Drawer/AppDrawer';
import { AutomationCanvasEditor } from '../../../shared/components/automation/AutomationCanvasEditor';
import {
	IAutomationsReturnLocationState,
	resolveAutomationsReturnLocation,
} from '../../../shared/components/automation/automationsReturnLocation';
import { ArchivedActionBar } from './components/ArchivedActionBar';
import { styleSheet } from './styles';

export function AutomationFlowEditorPage() {
	const { id: templateId } = useParams<{ id?: string }>();
	const history = useHistory();
	const location = useLocation<IAutomationsReturnLocationState | undefined>();

	const returnToAutomations = () => history.push(resolveAutomationsReturnLocation(location.state));

	const {
		data: template,
		isLoading,
		error,
		refetch,
	} = useAutomationTemplateQuery({
		id: templateId || '',
		enabled: !!templateId,
	});

	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logInput } = useEventLogging('AutomationFlowEditorPage');
	const unarchiveMutation = useUnarchiveAutomationTemplateMutation({
		onSuccess: () => {
			toast.success('Automation restored. It is disabled until you enable it.');
		},
		onError: unarchiveError => {
			logApiError('UnarchiveAutomationTemplate-Error', unarchiveError);
			errorMessages.pushApiError(unarchiveError);
		},
	});
	const isArchived = template?.status === Api.TemplateStatus.Archived;

	const pathComponentNameProvider = useCallback(
		(pathComponent: string) => {
			if (templateId && pathComponent === templateId) {
				return template?.name;
			}
			if (pathComponent === 'canvas') {
				return 'Edit Automation';
			}
			return null;
		},
		[templateId, template?.name]
	);

	return (
		<AutomationCanvasEditor
			templateId={templateId || ''}
			template={template}
			isLoading={isLoading}
			isError={!!error}
			error={error}
			refetch={refetch}
			onPublishSuccess={returnToAutomations}
			onDeleteSuccess={returnToAutomations}
			contentStyles={[styleSheet.contentExtras]}
			isReadOnly={isArchived}
			renderActionBar={
				isArchived
					? () => (
							<ArchivedActionBar
								canRestore={canRestoreAutomationTemplate({
									template,
									userRole: userSession.user.role,
									impersonationContext: userSession.impersonationContext,
								})}
								isRestoring={unarchiveMutation.isPending}
								onRestore={() => {
									logInput('UnarchiveAutomationTemplate', 'Click', { id: template.id });
									unarchiveMutation.mutate({ id: template.id, impersonationContext: userSession.impersonationContext });
								}}
							/>
						)
					: undefined
			}
			renderLoading={message => (
				<div className={css(styleSheet.layout)}>
					<div className={css(styleSheet.contentLoading)}>
						<div className={css(styleSheet.loadingContainer)} role='status' aria-live='polite'>
							<LoadingSpinner type='large' />
							<p className={css(styleSheet.loadingText)}>{message}</p>
						</div>
					</div>
				</div>
			)}
			renderError={(message, onRetry) => (
				<div className={css(styleSheet.layout)}>
					<div className={css(styleSheet.contentLoading)}>
						<div className={css(styleSheet.errorContainer)} role='alert' aria-live='assertive'>
							<h2 className={css(styleSheet.errorTitle)}>Failed to load automation</h2>
							<p className={css(styleSheet.errorMessage)}>{message}</p>
							<Button variant='primary' onClick={onRetry} aria-label='Retry loading automation'>
								Retry
							</Button>
						</div>
					</div>
				</div>
			)}
		>
			{({ contentArea, drawerPanels, drawerToolbarButtons }) => (
				<>
					<MultiContainerHeader
						appBarHeader={<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />}
					/>
					<div className={css(styleSheet.layout)}>
						{contentArea}
						<AppDrawer>
							{drawerPanels}
							<AppDrawerToolbar>{drawerToolbarButtons}</AppDrawerToolbar>
						</AppDrawer>
					</div>
				</>
			)}
		</AutomationCanvasEditor>
	);
}
